// features/superAdmin/components/LandingPageBuilderContent.tsx
import React, { useState, useRef } from 'react';

interface LandingPageData {
    header: {
        logo: string;
        logoSize: number;
        logoPosition: string;
        showNavigation: boolean;
        navigationLinks: any[];
        backgroundColor: string;
        textColor: string;
        padding: string;
        isSticky: boolean;
    };
    hero: {
        title: string;
        titleColor: string;
        titleFontSize: string;
        subtitle: string;
        subtitleColor: string;
        subtitleFontSize: string;
        buttonText: string;
        buttonTextColor: string;
        buttonBackgroundColor: string;
        buttonHoverColor: string;
        buttonSize: string;
        buttonBorderRadius: string;
        buttonLink: string;
        mediaType: string;
        backgroundImage: string;
        backgroundVideo: string;
        backgroundColor: string;
        overlayOpacity: number;
        contentPosition: string;
        contentWidth: string;
        paddingTop: string;
        paddingBottom: string;
        isVisible: boolean;
    };
    signInButton: {
        text: string;
        textColor: string;
        backgroundColor: string;
        hoverColor: string;
        borderRadius: string;
        size: string;
        isVisible: boolean;
    };
}

const LandingPageBuilderContent: React.FC = () => {
    const [previewMode, setPreviewMode] = useState(false);
    const [isProcessingBackground, setIsProcessingBackground] = useState(false);
    const [activeTab, setActiveTab] = useState('header');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const heroImageInputRef = useRef<HTMLInputElement>(null);
    const heroVideoInputRef = useRef<HTMLInputElement>(null);

    const [config, setConfig] = useState<LandingPageData>({
        header: {
            logo: 'https://via.placeholder.com/150x50',
            logoSize: 48,
            logoPosition: 'left',
            showNavigation: false,
            navigationLinks: [],
            backgroundColor: '#1a1a2e',
            textColor: '#FFFFFF',
            padding: '16',
            isSticky: false,
        },
        hero: {
            title: 'Welcome to SIMB Platform',
            titleColor: '#FFFFFF',
            titleFontSize: '48',
            subtitle: 'Transform your business with our powerful platform',
            subtitleColor: '#E0E0E0',
            subtitleFontSize: '20',
            buttonText: 'Get Started',
            buttonTextColor: '#FFFFFF',
            buttonBackgroundColor: '#e94560',
            buttonHoverColor: '#c73e54',
            buttonSize: 'medium',
            buttonBorderRadius: '8',
            buttonLink: '/signup',
            mediaType: 'image',
            backgroundImage: '',
            backgroundVideo: '',
            backgroundColor: '#0f3460',
            overlayOpacity: 0.5,
            contentPosition: 'center',
            contentWidth: '800',
            paddingTop: '80',
            paddingBottom: '80',
            isVisible: true,
        },
        signInButton: {
            text: 'Sign In',
            textColor: '#FFFFFF',
            backgroundColor: '#e94560',
            hoverColor: '#c73e54',
            borderRadius: '8',
            size: 'medium',
            isVisible: true,
        },
    });

    const [customCSS, setCustomCSS] = useState(`
.hero-section {
    padding: 100px 0;
    min-height: 80vh;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
}
.hero-video {
    position: absolute;
    top: 50%;
    left: 50%;
    min-width: 100%;
    min-height: 100%;
    width: auto;
    height: auto;
    transform: translateX(-50%) translateY(-50%);
    object-fit: cover;
}
.hero-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
}
.hero-content {
    position: relative;
    z-index: 2;
}
    `);

    const detectBackground = (imageData: ImageData): boolean => {
        const data = imageData.data;
        const { width, height } = imageData;
        const cornerPixels = [
            data[0], data[1], data[2],
            data[width * 4 - 4], data[width * 4 - 3], data[width * 4 - 2],
            data[(height - 1) * width * 4], data[(height - 1) * width * 4 + 1], data[(height - 1) * width * 4 + 2],
            data[(height - 1) * width * 4 + (width - 1) * 4], data[(height - 1) * width * 4 + (width - 1) * 4 + 1], data[(height - 1) * width * 4 + (width - 1) * 4 + 2]
        ];

        const avgR = cornerPixels.filter((_, i) => i % 3 === 0).reduce((a, b) => a + b, 0) / 4;
        const avgG = cornerPixels.filter((_, i) => i % 3 === 1).reduce((a, b) => a + b, 0) / 4;
        const avgB = cornerPixels.filter((_, i) => i % 3 === 2).reduce((a, b) => a + b, 0) / 4;

        return avgR > 200 && avgG > 200 && avgB > 200;
    };

    const removeBackground = (imgElement: HTMLImageElement): Promise<string> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            canvas.width = imgElement.width;
            canvas.height = imgElement.height;
            ctx.drawImage(imgElement, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            if (detectBackground(imageData)) {
                for (let i = 0; i < data.length; i += 4) {
                    if (data[i] > 200 && data[i + 1] > 200 && data[i + 2] > 200) {
                        data[i + 3] = 0;
                    }
                }
                ctx.putImageData(imageData, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            } else {
                resolve(imgElement.src);
            }
        });
    };

    const processImageWithBackgroundRemoval = async (imgElement: HTMLImageElement): Promise<string> => {
        try {
            return await removeBackground(imgElement);
        } catch (error) {
            console.error('Background removal failed:', error);
            return imgElement.src;
        }
    };

    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.match('image.*')) {
            setIsProcessingBackground(true);
            try {
                const base64 = await convertToBase64(file);
                const img = new Image();
                img.onload = async () => {
                    try {
                        const processedImage = await processImageWithBackgroundRemoval(img);
                        handleDesignChange('header', 'logo', processedImage);
                    } catch (error) {
                        handleDesignChange('header', 'logo', base64);
                    } finally {
                        setIsProcessingBackground(false);
                    }
                };
                img.src = base64;
            } catch (error) {
                console.error('Error processing logo:', error);
                setIsProcessingBackground(false);
            }
        }
    };

    const handleHeroMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
        const file = event.target.files?.[0];
        if (file) {
            const base64 = await convertToBase64(file);
            if (type === 'image') {
                handleDesignChange('hero', 'backgroundImage', base64);
            } else {
                handleDesignChange('hero', 'backgroundVideo', base64);
            }
        }
    };

    const handleDesignChange = (section: 'header' | 'hero' | 'signInButton', field: string, value: any) => {
        setConfig(prev => ({
            ...prev,
            [section]: { ...prev[section], [field]: value }
        }));
    };

    const handleSave = () => {
        console.log('Saving landing page:', config, customCSS);
        localStorage.setItem('landingPageConfig', JSON.stringify(config));
        localStorage.setItem('landingPageCSS', customCSS);
        alert('✅ Landing page configuration saved successfully!');
    };

    const getButtonSizeClasses = (size: string) => {
        switch (size) {
            case 'small': return 'px-3 py-1.5 text-sm';
            case 'large': return 'px-8 py-3 text-lg';
            default: return 'px-4 py-2 text-base';
        }
    };

    const getLogoPositionClasses = (position: string) => {
        switch (position) {
            case 'center': return 'mx-auto';
            case 'right': return 'ml-auto';
            default: return 'mr-auto';
        }
    };

    const getContentPositionClasses = (position: string) => {
        switch (position) {
            case 'left': return 'text-left ml-0 mr-auto';
            case 'right': return 'text-right mr-0 ml-auto';
            default: return 'text-center mx-auto';
        }
    };

    const LandingPreview = () => (
        <div className="space-y-0">
            <style>{customCSS}</style>
            <style>{`
                .sign-in-btn:hover {
                    background-color: ${config.signInButton.hoverColor} !important;
                }
                .hero-cta-btn:hover {
                    background-color: ${config.hero.buttonHoverColor} !important;
                }
                .hero-content {
                    max-width: ${config.hero.contentWidth}px;
                }
            `}</style>

            <header style={{
                backgroundColor: config.header.backgroundColor,
                paddingTop: `${config.header.padding}px`,
                paddingBottom: `${config.header.padding}px`,
                position: config.header.isSticky ? 'sticky' : 'relative',
                top: 0,
                zIndex: 1000,
            }} className="shadow-lg">
                <div className="container mx-auto px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div className={`flex items-center ${getLogoPositionClasses(config.header.logoPosition)}`}>
                            {config.header.logo && (
                                <img src={config.header.logo} alt="Logo" style={{ height: `${config.header.logoSize}px` }} className="object-contain" />
                            )}
                        </div>
                        {config.signInButton.isVisible && (
                            <button
                                className={`sign-in-btn rounded-lg font-semibold transition ${getButtonSizeClasses(config.signInButton.size)}`}
                                style={{
                                    backgroundColor: config.signInButton.backgroundColor,
                                    color: config.signInButton.textColor,
                                    borderRadius: `${config.signInButton.borderRadius}px`,
                                }}
                            >
                                {config.signInButton.text}
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {config.hero.isVisible && (
                <section className="hero-section" style={{
                    backgroundColor: config.hero.backgroundColor,
                    paddingTop: `${config.hero.paddingTop}px`,
                    paddingBottom: `${config.hero.paddingBottom}px`,
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    {config.hero.mediaType === 'video' && config.hero.backgroundVideo && (
                        <video autoPlay loop muted playsInline className="hero-video">
                            <source src={config.hero.backgroundVideo} type="video/mp4" />
                        </video>
                    )}
                    {config.hero.mediaType === 'image' && config.hero.backgroundImage && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: `url(${config.hero.backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }} />
                    )}

                    <div className="hero-overlay" style={{ backgroundColor: `rgba(0, 0, 0, ${config.hero.overlayOpacity})` }} />

                    <div className={`container mx-auto px-6 relative z-10 ${getContentPositionClasses(config.hero.contentPosition)}`}>
                        <div className="hero-content">
                            <h1 className="font-bold mb-4" style={{
                                color: config.hero.titleColor,
                                fontSize: `${config.hero.titleFontSize}px`,
                            }}>
                                {config.hero.title}
                            </h1>
                            <p className="mb-8" style={{
                                color: config.hero.subtitleColor,
                                fontSize: `${config.hero.subtitleFontSize}px`,
                            }}>
                                {config.hero.subtitle}
                            </p>
                            <button
                                className={`hero-cta-btn font-semibold transition ${getButtonSizeClasses(config.hero.buttonSize)}`}
                                style={{
                                    backgroundColor: config.hero.buttonBackgroundColor,
                                    color: config.hero.buttonTextColor,
                                    borderRadius: `${config.hero.buttonBorderRadius}px`,
                                }}
                            >
                                {config.hero.buttonText}
                            </button>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );

    return (
        <div className="h-full flex flex-col">
            <div className="bg-white shadow-md p-4 flex justify-between items-center border-b">
                <h2 className="text-2xl font-bold">Landing Page Builder</h2>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPreviewMode(!previewMode)}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition"
                    >
                        {previewMode ? '✏️ Edit Mode' : '👁️ Preview Mode'}
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                    >
                        💾 Save & Publish
                    </button>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {!previewMode && (
                    <div className="w-96 bg-gray-100 overflow-y-auto p-4 border-r">
                        <div className="space-y-4">
                            <div className="flex gap-2 border-b pb-2">
                                <button
                                    onClick={() => setActiveTab('header')}
                                    className={`px-3 py-1 rounded text-sm transition ${activeTab === 'header' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                >
                                    🏠 Header
                                </button>
                                <button
                                    onClick={() => setActiveTab('hero')}
                                    className={`px-3 py-1 rounded text-sm transition ${activeTab === 'hero' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                >
                                    🎯 Hero
                                </button>
                                <button
                                    onClick={() => setActiveTab('signin')}
                                    className={`px-3 py-1 rounded text-sm transition ${activeTab === 'signin' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                >
                                    🔐 Sign In
                                </button>
                                <button
                                    onClick={() => setActiveTab('css')}
                                    className={`px-3 py-1 rounded text-sm transition ${activeTab === 'css' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                >
                                    🎨 CSS
                                </button>
                            </div>

                            {/* Header Tab */}
                            {activeTab === 'header' && (
                                <div className="bg-white p-4 rounded shadow space-y-4">
                                    <h3 className="font-bold text-lg mb-3">Header Settings</h3>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Logo Upload</label>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleLogoUpload}
                                            accept="image/*"
                                            className="w-full p-2 border rounded text-sm"
                                        />
                                        {isProcessingBackground && (
                                            <p className="text-xs text-blue-600 mt-1">Processing background removal...</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Logo Size (px)</label>
                                        <input
                                            type="range"
                                            min="32"
                                            max="120"
                                            value={config.header.logoSize}
                                            onChange={(e) => handleDesignChange('header', 'logoSize', parseInt(e.target.value))}
                                            className="w-full"
                                        />
                                        <span className="text-xs text-gray-500">{config.header.logoSize}px</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Logo Position</label>
                                        <select
                                            value={config.header.logoPosition}
                                            onChange={(e) => handleDesignChange('header', 'logoPosition', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        >
                                            <option value="left">Left</option>
                                            <option value="center">Center</option>
                                            <option value="right">Right</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Header Background Color</label>
                                        <input
                                            type="color"
                                            value={config.header.backgroundColor}
                                            onChange={(e) => handleDesignChange('header', 'backgroundColor', e.target.value)}
                                            className="w-full p-1 border rounded h-10"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Header Padding (px)</label>
                                        <input
                                            type="range"
                                            min="8"
                                            max="48"
                                            value={config.header.padding}
                                            onChange={(e) => handleDesignChange('header', 'padding', e.target.value)}
                                            className="w-full"
                                        />
                                        <span className="text-xs text-gray-500">{config.header.padding}px</span>
                                    </div>

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={config.header.isSticky}
                                            onChange={(e) => handleDesignChange('header', 'isSticky', e.target.checked)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">Sticky Header</span>
                                    </label>
                                </div>
                            )}

                            {/* Hero Tab */}
                            {activeTab === 'hero' && (
                                <div className="bg-white p-4 rounded shadow space-y-4">
                                    <h3 className="font-bold text-lg mb-3">Hero Section Settings</h3>

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={config.hero.isVisible}
                                            onChange={(e) => handleDesignChange('hero', 'isVisible', e.target.checked)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">Show Hero Section</span>
                                    </label>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Title</label>
                                        <input
                                            type="text"
                                            value={config.hero.title}
                                            onChange={(e) => handleDesignChange('hero', 'title', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Title Color</label>
                                        <input
                                            type="color"
                                            value={config.hero.titleColor}
                                            onChange={(e) => handleDesignChange('hero', 'titleColor', e.target.value)}
                                            className="w-full p-1 border rounded h-10"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Title Font Size (px)</label>
                                        <input
                                            type="range"
                                            min="24"
                                            max="72"
                                            value={config.hero.titleFontSize}
                                            onChange={(e) => handleDesignChange('hero', 'titleFontSize', e.target.value)}
                                            className="w-full"
                                        />
                                        <span className="text-xs text-gray-500">{config.hero.titleFontSize}px</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Subtitle</label>
                                        <textarea
                                            value={config.hero.subtitle}
                                            onChange={(e) => handleDesignChange('hero', 'subtitle', e.target.value)}
                                            className="w-full p-2 border rounded"
                                            rows={2}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Button Text</label>
                                        <input
                                            type="text"
                                            value={config.hero.buttonText}
                                            onChange={(e) => handleDesignChange('hero', 'buttonText', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Button Color</label>
                                        <input
                                            type="color"
                                            value={config.hero.buttonBackgroundColor}
                                            onChange={(e) => handleDesignChange('hero', 'buttonBackgroundColor', e.target.value)}
                                            className="w-full p-1 border rounded h-10"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Media Type</label>
                                        <select
                                            value={config.hero.mediaType}
                                            onChange={(e) => handleDesignChange('hero', 'mediaType', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        >
                                            <option value="image">Image</option>
                                            <option value="video">Video</option>
                                            <option value="none">None</option>
                                        </select>
                                    </div>

                                    {config.hero.mediaType === 'image' && (
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Background Image</label>
                                            <input
                                                type="file"
                                                ref={heroImageInputRef}
                                                onChange={(e) => handleHeroMediaUpload(e, 'image')}
                                                accept="image/*"
                                                className="w-full p-2 border rounded text-sm"
                                            />
                                        </div>
                                    )}

                                    {config.hero.mediaType === 'video' && (
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Background Video</label>
                                            <input
                                                type="file"
                                                ref={heroVideoInputRef}
                                                onChange={(e) => handleHeroMediaUpload(e, 'video')}
                                                accept="video/*"
                                                className="w-full p-2 border rounded text-sm"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={config.hero.overlayOpacity}
                                            onChange={(e) => handleDesignChange('hero', 'overlayOpacity', parseFloat(e.target.value))}
                                            className="w-full"
                                        />
                                        <span className="text-xs text-gray-500">{Math.round(config.hero.overlayOpacity * 100)}%</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Content Position</label>
                                        <select
                                            value={config.hero.contentPosition}
                                            onChange={(e) => handleDesignChange('hero', 'contentPosition', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        >
                                            <option value="left">Left</option>
                                            <option value="center">Center</option>
                                            <option value="right">Right</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Content Width (px)</label>
                                        <input
                                            type="range"
                                            min="400"
                                            max="1200"
                                            step="50"
                                            value={config.hero.contentWidth}
                                            onChange={(e) => handleDesignChange('hero', 'contentWidth', e.target.value)}
                                            className="w-full"
                                        />
                                        <span className="text-xs text-gray-500">{config.hero.contentWidth}px</span>
                                    </div>
                                </div>
                            )}

                            {/* Sign In Button Tab */}
                            {activeTab === 'signin' && (
                                <div className="bg-white p-4 rounded shadow space-y-4">
                                    <h3 className="font-bold text-lg mb-3">Sign In Button Settings</h3>

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={config.signInButton.isVisible}
                                            onChange={(e) => handleDesignChange('signInButton', 'isVisible', e.target.checked)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">Show Sign In Button</span>
                                    </label>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Button Text</label>
                                        <input
                                            type="text"
                                            value={config.signInButton.text}
                                            onChange={(e) => handleDesignChange('signInButton', 'text', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Text Color</label>
                                        <input
                                            type="color"
                                            value={config.signInButton.textColor}
                                            onChange={(e) => handleDesignChange('signInButton', 'textColor', e.target.value)}
                                            className="w-full p-1 border rounded h-10"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Background Color</label>
                                        <input
                                            type="color"
                                            value={config.signInButton.backgroundColor}
                                            onChange={(e) => handleDesignChange('signInButton', 'backgroundColor', e.target.value)}
                                            className="w-full p-1 border rounded h-10"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Hover Color</label>
                                        <input
                                            type="color"
                                            value={config.signInButton.hoverColor}
                                            onChange={(e) => handleDesignChange('signInButton', 'hoverColor', e.target.value)}
                                            className="w-full p-1 border rounded h-10"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Button Size</label>
                                        <select
                                            value={config.signInButton.size}
                                            onChange={(e) => handleDesignChange('signInButton', 'size', e.target.value)}
                                            className="w-full p-2 border rounded"
                                        >
                                            <option value="small">Small</option>
                                            <option value="medium">Medium</option>
                                            <option value="large">Large</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Border Radius (px)</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="32"
                                            value={config.signInButton.borderRadius}
                                            onChange={(e) => handleDesignChange('signInButton', 'borderRadius', e.target.value)}
                                            className="w-full"
                                        />
                                        <span className="text-xs text-gray-500">{config.signInButton.borderRadius}px</span>
                                    </div>
                                </div>
                            )}

                            {/* Custom CSS Tab */}
                            {activeTab === 'css' && (
                                <div className="bg-white p-4 rounded shadow space-y-4">
                                    <h3 className="font-bold text-lg mb-3">Custom CSS</h3>
                                    <textarea
                                        value={customCSS}
                                        onChange={(e) => setCustomCSS(e.target.value)}
                                        className="w-full p-3 border rounded font-mono text-sm"
                                        rows={20}
                                        placeholder="/* Add your custom CSS here */"
                                    />
                                    <p className="text-xs text-gray-500 mt-2">
                                        💡 Tip: Add custom CSS to override default styles and add animations
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className={`flex-1 overflow-y-auto ${!previewMode ? 'border-l' : ''}`}>
                    <LandingPreview />
                </div>
            </div>
        </div>
    );
};

export default LandingPageBuilderContent;
